import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Plus, Building2, Edit, Users, Trash2, Save, Key, Ban, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import Swal from 'sweetalert2';

import API from '../api_config';

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  
  // User management state
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'cajero'
  });

  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUserData, setEditingUserData] = useState(null);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    rut: '',
    address: '',
    phone: '',
    email: '',
    logo_url: '',
    active_modules: ['pos', 'inventory', 'purchases', 'sales', 'reports', 'ai_predictions'],
    admin_email: '',
    admin_password: '',
    admin_full_name: '',
    monthly_fee_uf: 0,
    free_months: 0,
    billing_document_type: 'boleta',
    business_activity: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Filtrar business_activity si es boleta
      const submitData = { ...formData };
      if (submitData.billing_document_type === 'boleta') {
        submitData.business_activity = null;
      }
      
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, submitData);
        toast.success('Empresa actualizada');
      } else {
        const registerData = {
          company_name: submitData.name,
          company_rut: submitData.rut,
          company_address: submitData.address,
          admin_email: submitData.admin_email,
          admin_password: submitData.admin_password,
          admin_full_name: submitData.admin_full_name,
          active_modules: submitData.active_modules,
          monthly_fee_uf: submitData.monthly_fee_uf,
          free_months: submitData.free_months,
          billing_document_type: submitData.billing_document_type,
          business_activity: submitData.business_activity
        };
        await axios.post(`${API}/companies/register`, registerData);
        toast.success('Empresa y administrador creados exitosamente');
      }
      setOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al procesar la solicitud');
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      rut: company.rut,
      address: company.address,
      phone: company.phone || '',
      email: company.email || '',
      logo_url: company.logo_url || '',
      active_modules: company.active_modules || ['pos', 'inventory', 'purchases', 'sales', 'reports', 'ai_predictions'],
      monthly_fee_uf: company.monthly_fee_uf || 0,
      free_months: company.free_months || 0,
      billing_document_type: company.billing_document_type || 'boleta',
      business_activity: company.business_activity || '',
      admin_email: '',
      admin_password: '',
      admin_full_name: ''
    });
    setOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rut: '',
      address: '',
      phone: '',
      email: '',
      logo_url: '',
      active_modules: ['pos', 'inventory', 'purchases', 'sales', 'reports', 'ai_predictions'],
      monthly_fee_uf: 0,
      free_months: 0,
      billing_document_type: 'boleta',
      business_activity: '',
      admin_email: '',
      admin_password: '',
      admin_full_name: ''
    });
    setEditingCompany(null);
  };

  const handleToggleStatus = async (company) => {
    const isCurrentlyActive = company.is_active !== false;
    
    if (isCurrentlyActive) {
      const { value: reason } = await Swal.fire({
        title: 'Suspender Empresa',
        input: 'text',
        inputLabel: 'Razón de suspensión (ej. Falta de pago)',
        inputPlaceholder: 'Ingresa el motivo...',
        showCancelButton: true,
        confirmButtonText: 'Sí, suspender',
        cancelButtonText: 'Cancelar',
        inputValidator: (value) => {
          if (!value) {
            return '¡Necesitas escribir una razón!';
          }
        }
      });
      if (reason) {
        try {
          await axios.put(`${API}/companies/${company.id}`, {
            is_active: false,
            suspension_reason: reason
          });
          toast.success('Empresa suspendida exitosamente');
          fetchCompanies();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Error al suspender la empresa');
        }
      }
    } else {
      const result = await Swal.fire({
        title: 'Reactivar Empresa',
        text: '¿Deseas habilitar nuevamente el acceso a esta empresa?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, reactivar',
        cancelButtonText: 'Cancelar'
      });
      if (result.isConfirmed) {
        try {
          await axios.put(`${API}/companies/${company.id}`, {
            is_active: true,
            suspension_reason: null
          });
          toast.success('Empresa reactivada exitosamente');
          fetchCompanies();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Error al reactivar la empresa');
        }
      }
    }
  };

  // User management functions
  const fetchCompanyUsers = async (companyId) => {
    try {
      const response = await axios.get(`${API}/companies/${companyId}/users`);
      setCompanyUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
      console.error(error);
    }
  };

  const handleViewUsers = (company) => {
    setSelectedCompany(company);
    setUsersDialogOpen(true);
    fetchCompanyUsers(company.id);
  };

  const handleCreateUserForCompany = async () => {
    if (!newUserData.full_name || !newUserData.email || !newUserData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/users`, newUserData);
      toast.success('Usuario creado correctamente');
      setCreateUserDialogOpen(false);
      setNewUserData({ full_name: '', email: '', password: '', role: 'cajero' });
      fetchCompanyUsers(selectedCompany.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleEditUser = (user) => {
    setEditingUserData({
      ...user
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUserData.full_name || !editingUserData.email) {
      toast.error('Nombre y Correo son requeridos');
      return;
    }

    try {
      await axios.put(`${API}/companies/${selectedCompany.id}/users/${editingUserData.id}`, {
        full_name: editingUserData.full_name,
        email: editingUserData.email,
        role: editingUserData.role,
      });
      toast.success('Usuario actualizado correctamente');
      setEditUserDialogOpen(false);
      fetchCompanyUsers(selectedCompany.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleChangePasswordClick = (user) => {
    setChangingPasswordUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }

    try {
      await axios.put(`${API}/companies/${selectedCompany.id}/users/${changingPasswordUser.id}/password`, {
        password: newPassword
      });
      toast.success('Contraseña actualizada correctamente');
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const handleDeleteUser = async (userId) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      customClass: {
        container: 'z-[9999]'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API}/companies/${selectedCompany.id}/users/${userId}`);
          toast.success('El usuario ha sido eliminado');
          fetchCompanyUsers(selectedCompany.id);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Error al eliminar');
        }
      }
    });
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Empresas</h1>
          <p className="text-muted-foreground mt-2">Gestiona las empresas del sistema</p>
        </div>
        <div className="flex items-center gap-2">
          <Input 
            placeholder="Buscar empresa por nombre o RUT..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-64"
          />
          <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full shadow-lg" data-testid="add-company-btn">
              <Plus className="h-5 w-5 mr-2" />
              Nueva Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}</DialogTitle>
              <DialogDescription>Completa los datos de la empresa y su administrador.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Información de la Empresa */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Datos de la Empresa</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de Empresa *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>RUT Empresa *</Label>
                  <Input
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                    required
                    placeholder="12.345.678-9"
                    data-testid="company-rut-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Dirección Empresa *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  data-testid="company-address-input"
                />
              </div>

              {!editingCompany && (
                <>
                  <div className="space-y-2 pt-4 border-t">
                    <h3 className="font-semibold text-lg">Administrador Inicial</h3>
                    <p className="text-sm text-muted-foreground">Este usuario será el administrador de la nueva empresa.</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Nombre Completo Admin *</Label>
                    <Input
                      value={formData.admin_full_name}
                      onChange={(e) => setFormData({ ...formData, admin_full_name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email Admin *</Label>
                      <Input
                        type="email"
                        value={formData.admin_email}
                        onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contraseña Admin *</Label>
                      <Input
                        type="password"
                        value={formData.admin_password}
                        onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Campos opcionales solo para edición o visualización extra si se requieren */}
              {editingCompany && (
                 <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Contacto</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
              )}
               
               {editingCompany && (
                   <div className="space-y-2">
                    <Label>URL del Logo</Label>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
               )}

              {/* Módulos de la Empresa */}
              <div className="space-y-2 pt-4 border-t">
                <h3 className="font-semibold text-lg">Módulos Habilitados</h3>
                <p className="text-sm text-muted-foreground">Selecciona qué secciones estarán disponibles para los usuarios de esta empresa.</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                 {[
                    { id: 'pos', label: 'Punto de Venta (POS)' },
                    { id: 'inventory', label: 'Inventario y Bodegas' },
                    { id: 'purchases', label: 'Adquisiciones y Compras' },
                    { id: 'sales', label: 'Gestión de Ventas' },
                    { id: 'reports', label: 'Reportes y Panel Gerencial' },
                    { id: 'ai_predictions', label: 'Predicciones Inteligencia Artificial' },
                    { id: 'construction', label: 'Módulo Construcción Chilena' }
                 ].map(module => (
                    <div key={module.id} className="flex items-start space-x-2">
                      <Checkbox 
                        id={`module-${module.id}`}
                        checked={formData.active_modules.includes(module.id)}
                        onCheckedChange={(checked) => {
                          setFormData(prev => ({
                            ...prev,
                            active_modules: checked 
                              ? [...prev.active_modules, module.id]
                              : prev.active_modules.filter(m => m !== module.id)
                          }));
                        }}
                      />
                      <label 
                        htmlFor={`module-${module.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mt-0.5 cursor-pointer"
                      >
                        {module.label}
                      </label>
                    </div>
                  ))}
              </div>

              {/* Facturación del Software (Para la empresa matriz) */}
              <div className="space-y-2 pt-4 border-t">
                <h3 className="font-semibold text-lg flex justify-between items-center">
                  <span>Facturación del Software</span>
                  <span className="text-xs font-normal px-2 py-1 bg-blue-100 text-blue-800 rounded-lg">Para Empresa Matriz</span>
                </h3>
                <p className="text-sm text-muted-foreground">Configura cómo se le cobrará el uso de la plataforma a esta empresa.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor Mensual (UF) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.monthly_fee_uf}
                    onChange={(e) => setFormData({ ...formData, monthly_fee_uf: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Meses de Gracia (Gratis) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.free_months}
                    onChange={(e) => setFormData({ ...formData, free_months: parseInt(e.target.value) || 0 })}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    El cobro iniciará en {formData.free_months} mes(es).
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Documento *</Label>
                  <Select 
                    value={formData.billing_document_type} 
                    onValueChange={(val) => setFormData({...formData, billing_document_type: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione un documento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boleta">Boleta</SelectItem>
                      <SelectItem value="factura">Factura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.billing_document_type === 'factura' && (
                  <div className="space-y-2">
                    <Label>Giro Comercial *</Label>
                    <Input
                      value={formData.business_activity}
                      onChange={(e) => setFormData({ ...formData, business_activity: e.target.value })}
                      required={formData.billing_document_type === 'factura'}
                      placeholder="Ej. Venta al por Menor"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-company-btn">
                  {editingCompany ? 'Actualizar' : 'Registrar Empresa'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.filter(c => 
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
          c.rut.toLowerCase().includes(searchTerm.toLowerCase())
        ).length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{searchTerm ? 'No se encontraron empresas con esa búsqueda' : 'No hay empresas registradas'}</p>
            </CardContent>
          </Card>
        ) : (
          companies.filter(c => 
            c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            c.rut.toLowerCase().includes(searchTerm.toLowerCase())
          ).map(company => (
            <Card key={company.id} className="hover:shadow-lg transition-shadow" data-testid={`company-${company.id}`}>
              <CardHeader>
                {company.logo_url && (
                  <img
                    src={company.logo_url}
                    alt={company.name}
                    className="w-full h-32 object-contain mb-4 rounded-lg bg-muted p-2"
                  />
                )}
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>{company.name}</span>
                  {company.is_active === false && (
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">Suspendida</span>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground font-mono">RUT: {company.rut}</p>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">{company.address}</p>
                {company.phone && (
                  <p className="text-sm text-muted-foreground">{company.phone}</p>
                )}
                {company.email && (
                  <p className="text-sm text-muted-foreground">{company.email}</p>
                )}
                <div className="mt-2 text-xs font-medium flex items-center justify-between bg-muted/50 p-2 rounded-md border">
                   <div>Cobro: {company.monthly_fee_uf || 0} UF/mes</div>
                   <div className="capitalize font-semibold text-primary">{company.billing_document_type || 'Boleta'}</div>
                </div>
                {company.billing_start_date && (
                   <p className="text-xs text-muted-foreground text-right mt-1">Inicio cobro: {new Date(company.billing_start_date).toLocaleDateString()}</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(company)}
                    data-testid={`edit-company-${company.id}`}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewUsers(company)}
                    className="bg-primary/10 hover:bg-primary/20"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Usuarios
                  </Button>
                  <Button
                    variant={company.is_active === false ? "default" : "destructive"}
                    size="sm"
                    onClick={() => handleToggleStatus(company)}
                  >
                    {company.is_active === false ? (
                       <><CheckCircle className="h-4 w-4 mr-2" /> Reactivar</>
                    ) : (
                       <><Ban className="h-4 w-4 mr-2" /> Suspender</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto w-[90vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuarios de {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription>Gestiona el acceso de los empleados a esta empresa.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setCreateUserDialogOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Usuario
              </Button>
            </div>
            
            {companyUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay usuarios registrados en esta empresa
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-medium min-w-[150px]">Nombre</th>
                      <th className="text-left p-3 font-medium min-w-[200px]">Email</th>
                      <th className="text-left p-3 font-medium min-w-[100px]">Rol</th>
                      <th className="text-right p-3 font-medium min-w-[200px]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyUsers.map(user => (
                      <tr key={user.id} className="border-t hover:bg-muted/50">
                        <td className="p-3">{user.full_name}</td>
                        <td className="p-3 text-muted-foreground break-all">{user.email}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                            user.role === 'admin' ? 'bg-red-100 text-red-800' :
                            user.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'cajero' ? 'bg-green-100 text-green-800' :
                            user.role === 'bodeguero' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role.toUpperCase()}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1 flex-wrap sm:flex-nowrap">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8"
                              onClick={() => handleChangePasswordClick(user)}
                              title="Cambiar Contraseña"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 hover:bg-red-100 hover:text-red-600 border-red-200"
                              onClick={() => handleDeleteUser(user.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Usuario para {selectedCompany?.name}</DialogTitle>
            <DialogDescription>Añade un nuevo empleado al sistema.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre Completo</Label>
              <Input
                value={newUserData.full_name}
                onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})}
                placeholder="Juan Pérez"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                placeholder="juan@empresa.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                placeholder="••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={newUserData.role} onValueChange={(val) => setNewUserData({...newUserData, role: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="cajero">Cajero</SelectItem>
                  <SelectItem value="bodeguero">Bodeguero</SelectItem>
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCreateUserDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateUserForCompany}>
                <Save className="h-4 w-4 mr-2" />
                Crear Usuario
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>Modifica los datos del empleado.</DialogDescription>
          </DialogHeader>
          {editingUserData && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre Completo</Label>
                <Input
                  value={editingUserData.full_name}
                  onChange={(e) => setEditingUserData({...editingUserData, full_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingUserData.email}
                  onChange={(e) => setEditingUserData({...editingUserData, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={editingUserData.role} onValueChange={(val) => setEditingUserData({...editingUserData, role: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="superadmin">Superadmin</SelectItem>
                    <SelectItem value="cajero">Cajero</SelectItem>
                    <SelectItem value="bodeguero">Bodeguero</SelectItem>
                    <SelectItem value="vendedor">Vendedor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleUpdateUser}>
                  <Save className="h-4 w-4 mr-2" />
                  Actualizar Usuario
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña: {changingPasswordUser?.full_name}</DialogTitle>
            <DialogDescription>Crea una nueva contraseña segura para el usuario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nueva Contraseña</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••"
              />
              <p className="text-xs text-muted-foreground mt-1">
                La nueva contraseña reemplazará a la actual.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdatePassword}>
                <Save className="h-4 w-4 mr-2" />
                Guardar Contraseña
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
